import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/design/Button";
import { ChallengeStep } from "./ChallengeStep";
import { MatchSettingsModal } from "./MatchModals";
import { PeopleStep } from "./PeopleStep";
import { ResultStep } from "./ResultStep";
import { SaveStatus } from "./SaveStatus";
import type { SlideId, StepId } from "./match-design-types";
import type { UseMatchEditorResult } from "./use-match-editor";
import { STEPS, nextStepId, prevStepId, stepIndex } from "./match-design-rules";

function stepLabelId(id: StepId, isRank: boolean): string {
  switch (id) {
    case "challenge":
      return "match.step.challenge";
    case "people":
      // Rank ranks individual candidates, not a team (ADR-0029).
      return isRank ? "match.step.peopleRank" : "match.step.people";
    case "result":
      return "match.step.result";
  }
}

interface MatchWorkflowProps {
  editor: UseMatchEditorResult;
  onNavigate?: (slide: SlideId) => void;
}

export function MatchWorkflow({
  editor,
  onNavigate: onNavigateExternal,
}: MatchWorkflowProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { data, stepId, setStepId, update, rename, flush, discard, myRole } = editor;
  // Matches are created fully-named via NewMatchModal (ADR-0029, revised), so
  // the settings dialog is only ever an edit surface reached through the gear.
  const [settingsOpen, setSettingsOpen] = useState(false);
  function closeSettings() {
    setSettingsOpen(false);
  }
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const canDelete = myRole === "owner" || myRole === "admin";
  const onNavigate = (slide: SlideId) => {
    if (onNavigateExternal) onNavigateExternal(slide);
    else navigate(slide === "share" ? "/share" : "/match");
  };

  const isReview = editor.solverResult !== null;
  const stepIdx = stepIndex(stepId);
  // Modus ist ab Erstellung immutabel (ADR-0029): er kommt aus der Match.matchMode-Spalte
  // (genesis #159 / V0035) und wird nur noch angezeigt, nicht mehr umgeschaltet.
  // null (theoretischer Altbestand) -> optimize.
  const mode: MatchMode =
    data.matchMode === "RANK" ? "rank" : data.matchMode === "ASSESS" ? "assess" : "optimize";
  // Beim Wiederoeffnen laedt data async: assessOnly dem persistierten Modus nachziehen,
  // sonst liefe der Solver eines Ranken-Matches im Optimize-Pfad.
  const assessFromMode = mode !== "optimize";
  const { setAssessOnly } = editor;
  useEffect(() => {
    setAssessOnly(assessFromMode);
  }, [assessFromMode, setAssessOnly]);

  function next() {
    const nxt = nextStepId(stepId);
    if (nxt) setStepId(nxt);
  }
  function prev() {
    const prv = prevStepId(stepId);
    if (prv) setStepId(prv);
  }
  async function generate() {
    await editor.aiGenerate();
  }
  async function handleDelete() {
    setConfirmDeleteOpen(false);
    await discard();
  }

  return (
    <div className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="mb-3">
        <BackButton />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {myRole === "reader" ? (
          <div className="font-serif text-2xl leading-none">
            {data.matchTitle ||
              intl.formatMessage({ id: "match.unnamedMatch", defaultMessage: "Unbenanntes Match" })}
          </div>
        ) : (
          <input
            value={data.matchTitle ?? ""}
            onChange={(e) => rename(e.target.value)}
            onBlur={() => void flush()}
            placeholder={intl.formatMessage({
              id: "match.unnamedMatch",
              defaultMessage: "Unbenanntes Match",
            })}
            aria-label={intl.formatMessage({
              id: "match.renameLabel",
              defaultMessage: "Match umbenennen",
            })}
            className="-mx-1 min-w-[8ch] max-w-full rounded bg-transparent px-1 font-serif text-2xl leading-none placeholder:text-ink/30 focus:outline-none focus:ring-1 focus:ring-ink/30 [field-sizing:content]"
          />
        )}
        <ModeIndicator mode={mode} />
        <SaveStatus
          saveState={editor.saveState}
          lastSavedAt={editor.lastSavedAt}
          onRetry={editor.retrySave}
        />
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="icon"
            size="sm"
            type="button"
            onClick={() => setSettingsOpen(true)}
            title={intl.formatMessage({
              id: "match.workflow.settingsTitle",
              defaultMessage: "Match-Einstellungen",
            })}
            className="shrink-0"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </Button>
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDeleteOpen(true)}
              className="tracking-[0.2em]"
              title={intl.formatMessage({
                id: "match.workflow.deleteTitle",
                defaultMessage: "Match löschen",
              })}
            >
              <FormattedMessage id="common.delete" defaultMessage="Löschen" />
            </Button>
          )}
        </div>
      </div>

      {myRole === "reader" && (
        <div
          role="status"
          className="mt-4 rounded-md bg-ink/[0.06] px-4 py-2 text-sm text-ink/70"
        >
          <FormattedMessage
            id="match.readOnlyBanner"
            defaultMessage="Du kannst dieses Match nur ansehen. Bitte einen Admin oder Eigentümer:in um Schreibrechte."
          />
        </div>
      )}

      <div className="mt-5">
        <Stepper steps={STEPS} activeIndex={stepIdx} isRank={mode === "rank"} onPick={(id) => setStepId(id)} />
      </div>

      {stepId === "challenge" && (
        <p className="mt-5 max-w-xl text-sm text-ink/55">
          {mode === "optimize" ? (
            <FormattedMessage
              id="match.workflow.challengeSubtitle"
              defaultMessage="Beschreibe oben dein Projekt – die KI leitet daraus Team-Größe, Anforderungen und Powers ab. Darunter kontrollierst du das Ergebnis und justierst kleine Stellschrauben."
            />
          ) : mode === "rank" ? (
            <FormattedMessage
              id="match.workflow.challengeSubtitleRank"
              defaultMessage="Beschreibe oben die Rolle bzw. offene Stelle – die KI leitet daraus die Anforderungen ab, die ein:e Kandidat:in erfüllen soll. Darunter kontrollierst du das Ergebnis und justierst kleine Stellschrauben."
            />
          ) : (
            <FormattedMessage
              id="match.workflow.challengeSubtitleNoTeamSize"
              defaultMessage="Beschreibe oben dein Projekt – die KI leitet daraus Anforderungen und Powers ab. Darunter kontrollierst du das Ergebnis und justierst kleine Stellschrauben."
            />
          )}
        </p>
      )}

      <div className="mt-6">
        {stepId === "challenge" && (
          <ChallengeStep
            data={data}
            update={update}
            editor={editor}
            sharedItem={editor.sharedItem}
            myRole={myRole}
            onNavigate={onNavigate}
            onGenerate={generate}
            recruiting={mode === "rank"}
          />
        )}
        {stepId === "people" && <PeopleStep editor={editor} />}
        {stepId === "result" && (
          <ResultStep data={data} editor={editor} isReview={isReview} />
        )}
      </div>

      {settingsOpen && (
        <MatchSettingsModal editor={editor} onClose={closeSettings} />
      )}

      {stepId !== "result" && (
        // Mirrors the result step's floating save button (ResultStep.tsx):
        // sticky (not fixed) respects the main-content right edge; the wrapper
        // is pointer-events-none so clicks fall through, buttons re-enable it.
        <div className="pointer-events-none sticky bottom-4 z-10 mt-10 flex items-center justify-between sm:bottom-8">
          {stepIdx > 0 ? (
            // The secondary variant is transparent by design; floating over
            // scrolling list content that reads as a rendering bug (rows shine
            // through the label). The page-colored pill behind it keeps the
            // button opaque without touching the shared variant.
            <span className="pointer-events-auto rounded-full bg-page shadow-xl">
              <Button
                variant="secondary"
                onClick={prev}
                title={intl.formatMessage(
                  {
                    id: "match.workflow.prevTo",
                    defaultMessage: "Zurück zu {label}",
                  },
                  {
                    label: STEPS[stepIdx - 1]
                      ? intl.formatMessage({ id: stepLabelId(STEPS[stepIdx - 1]!.id, mode === "rank") })
                      : "",
                  },
                )}
                className="tracking-[0.2em]"
              >
                ← {STEPS[stepIdx - 1]?.n}{" "}
                {STEPS[stepIdx - 1] ? (
                  <FormattedMessage id={stepLabelId(STEPS[stepIdx - 1]!.id, mode === "rank")} />
                ) : null}
              </Button>
            </span>
          ) : (
            <span />
          )}
          <Button
            variant="primary"
            onClick={next}
            title={intl.formatMessage(
              {
                id: "match.workflow.nextTo",
                defaultMessage: "Weiter zu {label}",
              },
              {
                label: STEPS[stepIdx + 1]
                  ? intl.formatMessage({ id: stepLabelId(STEPS[stepIdx + 1]!.id, mode === "rank") })
                  : "",
              },
            )}
            className="pointer-events-auto tracking-[0.2em] shadow-xl"
          >
            {STEPS[stepIdx + 1]?.n}{" "}
            {STEPS[stepIdx + 1] ? (
              <FormattedMessage id={stepLabelId(STEPS[stepIdx + 1]!.id, mode === "rank")} />
            ) : null}{" "}
            →
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={<FormattedMessage id="match.workflow.deleteConfirm.title" defaultMessage="Match löschen?" />}
        message={
          <FormattedMessage
            id="match.workflow.deleteConfirm.message"
            defaultMessage="Das Match wird endgültig aus genesis entfernt. Diese Aktion kann nicht rückgängig gemacht werden."
          />
        }
        confirmLabel={<FormattedMessage id="match.workflow.deleteConfirm.confirm" defaultMessage="Löschen" />}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </div>
  );
}

type MatchMode = "optimize" | "assess" | "rank";

const MODE_LABEL_ID: Record<MatchMode, string> = {
  optimize: "match.workflow.modeOptimize",
  assess: "match.workflow.modeAssess",
  rank: "match.workflow.modeRank",
};

// Read-only indicator — matchMode is immutable after creation (ADR-0029),
// so the former in-place toggle is now just a label of the chosen mode.
function ModeIndicator({ mode }: { mode: MatchMode }) {
  const intl = useIntl();
  return (
    <span
      className="rounded-full px-2.5 py-1 text-[11px] uppercase tracking-[0.2em] ring-1 ring-ink/15 text-ink/70"
      title={intl.formatMessage({
        id: "match.workflow.modeIndicatorTitle",
        defaultMessage: "Mode (fixed at creation)",
      })}
    >
      <FormattedMessage id={MODE_LABEL_ID[mode]} />
    </span>
  );
}

function Stepper({
  steps,
  activeIndex,
  isRank,
  onPick,
}: {
  steps: ReadonlyArray<{ n: string; id: StepId; label: string }>;
  activeIndex: number;
  isRank: boolean;
  onPick: (id: StepId) => void;
}) {
  const last = steps.length - 1;
  return (
    <div className="grid" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
      {steps.map((s, i) => {
        const state = i < activeIndex ? "done" : i === activeIndex ? "active" : "todo";
        const faintLine = "rgb(from currentColor r g b / 0.15)";
        const numColor =
          state === "active"
            ? "var(--color-accent)"
            : state === "done"
              ? "currentColor"
              : "rgb(from currentColor r g b / 0.35)";
        const align = i === 0 ? "items-start" : i === last ? "items-end" : "items-center";
        const labelClass =
          state === "active"
            ? "mt-3 font-serif text-3xl leading-none tracking-tight"
            : "mt-3 text-[10px] tracking-[0.25em]";
        const labelColor =
          state === "todo" ? "rgb(from currentColor r g b / 0.4)" : "currentColor";
        return (
          <button key={s.id} onClick={() => onPick(s.id)} className={`flex flex-col ${align}`}>
            <div className="flex w-full items-center">
              {i !== 0 && (
                <div
                  className="h-px flex-1"
                  style={{ background: activeIndex >= i ? "currentColor" : faintLine }}
                />
              )}
              <span
                className={`text-[11px] font-medium tracking-wider ${i !== 0 ? "ml-3" : ""} ${
                  i !== last ? "mr-3" : ""
                }`}
                style={{ color: numColor }}
              >
                {s.n}
              </span>
              {i !== last && (
                <div
                  className="h-px flex-1"
                  style={{ background: activeIndex > i ? "currentColor" : faintLine }}
                />
              )}
            </div>
            <span className={labelClass} style={{ color: labelColor }}>
              <FormattedMessage id={stepLabelId(s.id, isRank)} />
            </span>
          </button>
        );
      })}
    </div>
  );
}
